import React from 'react';
import { useServerEvents, useEventTypes, useDeleteServerEvent, EventType } from '@/hooks/useServerEvents';
import { useLanguage } from '@/contexts/LanguageContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sparkles, Calendar, Clock, X } from 'lucide-react';
import { format, isAfter, isBefore } from 'date-fns';
import { fr, es, enUS } from 'date-fns/locale';
import { cn } from '@/lib/utils';
interface EventDisplayProps {
  serverId: string;
  variant?: 'banner' | 'detail' | 'compact';
  className?: string;
  isServerPrestige?: boolean; // ✅ NOUVEAU: Vérifier le statut prestige
  canDelete?: boolean; // ✅ NOUVEAU: Permettre la suppression des événements
}
export const EventDisplay: React.FC<EventDisplayProps> = ({
  serverId,
  variant = 'detail',
  className,
  isServerPrestige = false, // ✅ NOUVEAU: Par défaut, pas prestige
  canDelete = false // ✅ NOUVEAU: Par défaut, pas de suppression
}) => {
  const {
    data: events = [],
    isLoading
  } = useServerEvents(serverId);
  const {
    data: eventTypes = []
  } = useEventTypes();
  const deleteEvent = useDeleteServerEvent();
  const {
    language
  } = useLanguage();
  const getDateLocale = () => {
    switch (language) {
      case 'fr':
        return fr;
      case 'es':
        return es;
      default:
        return enUS;
    }
  };
  const getEventType = (eventTypeKey: string): EventType | undefined => {
    return eventTypes.find(type => type.key === eventTypeKey);
  };
  const getEventTitle = (event: any) => {
    const customTitle = event[`title_${language}`] || event.title_fr;
    if (customTitle) return customTitle;
    const eventType = getEventType(event.event_type_key);
    if (!eventType) return event.event_type_key;
    switch (language) {
      case 'en':
        return eventType.name_en;
      case 'es':
        return eventType.name_es;
      default:
        return eventType.name_fr;
    }
  };
  const getEventDescription = (event: any) => {
    return event[`description_${language}`] || event.description_fr || '';
  };
  const isEventActive = (event: any) => {
    const now = new Date();
    const startDate = event.start_date ? new Date(event.start_date) : null;
    const endDate = event.end_date ? new Date(event.end_date) : null;
    if (startDate && isAfter(startDate, now)) return false;
    if (endDate && isBefore(endDate, now)) return false;
    return true;
  };
  const getEventStatus = (event: any) => {
    const now = new Date();
    const startDate = event.start_date ? new Date(event.start_date) : null;
    const endDate = event.end_date ? new Date(event.end_date) : null;
    if (startDate && isAfter(startDate, now)) return 'upcoming';
    if (endDate && isBefore(endDate, now)) return 'ended';
    return 'active';
  };
  if (isLoading) {
    return <div className={cn("animate-pulse space-y-2", className)}>
        <div className="h-6 bg-muted rounded w-24"></div>
        <div className="h-4 bg-muted rounded w-32"></div>
      </div>;
  }

  // ✅ NOUVEAU: Seuls les serveurs prestige peuvent afficher des événements
  if (!isServerPrestige) {
    return null;
  }

  if (events.length === 0) {
    return null;
  }
  const visibleEvents = events.filter(event => {
    const now = new Date();
    const endDate = event.end_date ? new Date(event.end_date) : null;
    // Afficher événements en cours et à venir (cacher seulement les terminés)
    if (endDate && isBefore(endDate, now)) return false;
    return true;
  });
  if (visibleEvents.length === 0) {
    return null;
  }

  // Affichage bannière (compact, horizontal)
  if (variant === 'banner') {
    return <div className={cn("flex items-center gap-2 flex-wrap", "animate-fade-in", className)}>
        {visibleEvents.slice(0, 3).map(event => {
        const eventType = getEventType(event.event_type_key);
        return <Badge key={event.id} variant="secondary" className={cn("gap-1.5 px-3 py-1.5 text-xs font-medium", "bg-gradient-to-r from-primary/20 to-primary/10", "border border-primary/30", "animate-pulse retro-glow", "hover:scale-105 transition-transform duration-200")} style={{
          backgroundColor: eventType?.color ? `${eventType.color}20` : undefined,
          borderColor: eventType?.color ? `${eventType.color}50` : undefined
        }}>
              <span className="text-sm">{eventType?.icon}</span>
              <span>{getEventTitle(event)}</span>
              <Sparkles className="w-3 h-3 text-yellow-400 animate-pulse" />
            </Badge>;
      })}
      </div>;
  }

  // Affichage compact (pour cartes serveur)
  if (variant === 'compact') {
    return <div className={cn("flex gap-1", className)}>
        {visibleEvents.slice(0, 2).map(event => {
        const eventType = getEventType(event.event_type_key);
        const status = getEventStatus(event);
        return <div key={event.id} className={cn("flex items-center gap-1 text-xs", "px-2 py-1 rounded border border-primary/20", "bg-gradient-to-r from-primary/10 to-transparent", "retro-glow animate-fade-in", status === 'active' && "animate-pulse")} style={{
          backgroundColor: eventType?.color ? `${eventType.color}15` : undefined,
          borderColor: eventType?.color ? `${eventType.color}30` : undefined
        }}>
              <span className="text-sm">{eventType?.icon}</span>
              <span className="font-medium text-primary">
                {getEventTitle(event)}
              </span>
              {status === 'upcoming' && <Badge variant="outline" className="text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  {language === 'en' ? 'Soon' : language === 'es' ? 'Pronto' : 'Bientôt'}
                </Badge>}
            </div>;
      })}
      </div>;
  }

  // Affichage détaillé (page serveur)
  return <div className={cn("space-y-4", className)}>
      <div className="grid gap-3">
        {visibleEvents.map(event => {
        const eventType = getEventType(event.event_type_key);
        const status = getEventStatus(event);
        const description = getEventDescription(event);
        return <div key={event.id} className={cn("p-4 rounded-lg border-2 transition-all duration-300", "bg-gradient-to-br from-card to-card/50", "hover:shadow-lg hover:scale-[1.02]", "retro-glow animate-fade-in", status === 'active' && "animate-pulse")} style={{
          borderColor: eventType?.color || '#3B82F6',
          background: eventType?.color ? `linear-gradient(135deg, ${eventType.color}10, transparent)` : undefined
        }}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg animate-pulse" style={{
                backgroundColor: eventType?.color || '#3B82F6' + '20'
              }}>
                      {eventType?.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary">
                        {getEventTitle(event)}
                      </h4>
                      {event.start_date && <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          <span>
                            {format(new Date(event.start_date), 'PPP', {
                      locale: getDateLocale()
                    })}
                            {event.end_date && <span> - {format(new Date(event.end_date), 'PPP', {
                        locale: getDateLocale()
                      })}</span>}
                          </span>
                        </div>}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Badge variant={status === 'active' ? 'default' : 'secondary'} className={cn("animate-pulse", status === 'active' && "bg-green-500/20 text-green-400 border-green-500/50")}>
                      {status === 'active' ? '🔴 LIVE' : status === 'upcoming' ? language === 'en' ? '⏳ Soon' : language === 'es' ? '⏳ Pronto' : '⏳ Bientôt' : language === 'en' ? '✅ Finished' : language === 'es' ? '✅ Terminado' : '✅ Terminé'}
                    </Badge>
                    
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          deleteEvent.mutate(event.id);
                        }}
                        disabled={deleteEvent.isPending}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              
              {description && <p className="text-sm text-muted-foreground leading-relaxed">
                  {description}
                </p>}
            </div>;
      })}
      </div>
    </div>;
};